import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Button,
  Tooltip,
  Badge,
  Menu,
  MenuItem,
  Divider,
  Alert,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Gavel as BillIcon,
  People as PeopleIcon,
  Chat as ChatIcon,
  Logout as LogoutIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  AccountTree as CommitteesIcon,
  HowToVote as NominationsIcon,
  AdminPanelSettings as AdminIcon,
  Notifications as NotificationsIcon,
  SavedSearch as SavedSearchIcon,
  CollectionsBookmark as CollectionsIcon,
  NotificationsActive as AlertsIcon,
  Notes as NotesIcon,
  Login as LoginIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { apiService } from '../../services/apiService';

const drawerWidth = 240;

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login, logout, keycloak, authenticated } = useAuth();
  const [adminOverride, setAdminOverride] = useState(false);
  // In-app notifications (feed)
  const [notifAnchor, setNotifAnchor] = useState<null | HTMLElement>(null);
  const [feedItems, setFeedItems] = useState<any[] | null>(null);
  const [unread, setUnread] = useState<number>(0);
  // Site health/maintenance banner state
  const [maintenance, setMaintenance] = useState<{ unhealthy: boolean; detail?: string | null }>({ unhealthy: false, detail: null });

  useEffect(() => {
    const titles: Record<string, string> = {
      '/congress': 'Congresses', '/members': 'Congressional Members', '/committees': 'Committees',
      '/nominations': 'Nominations', '/chat': 'AI Chat Assistant', '/widgets': 'Embeddable Widgets',
      '/civic-response-studio': 'Civic Response Studio', '/bookmarks': 'My Bookmarks',
      '/me/saved-searches': 'Saved Searches', '/me/collections': 'Collections', '/me/alerts': 'Alerts',
      '/me/notes': 'My Notes', '/me/profile': 'My Profile', '/notifications': 'Notifications',
      '/about': 'About', '/privacy': 'Privacy Policy', '/terms': 'Terms & Conditions',
      '/contact': 'Contact Us', '/sitemap': 'Sitemap', '/admin': 'Administrator Dashboard',
    };
    const title = titles[location.pathname];
    if (title) document.title = `${title} | AskTheBill.ai`;
  }, [location.pathname]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const tokenParsed: any = keycloak?.tokenParsed || {};
  const groups: string[] = tokenParsed?.groups || tokenParsed?.realm_access?.groups || [];
  const roles: string[] = tokenParsed?.realm_access?.roles || [];
  const clientRoles: string[] = (() => {
    try {
      const ra = tokenParsed?.resource_access || {};
      const all: string[] = [];
      Object.values(ra).forEach((r: any) => {
        if (r && Array.isArray(r.roles)) all.push(...r.roles);
      });
      return all;
    } catch {
      return [];
    }
  })();
  const ADMIN_NAME = (process.env.REACT_APP_ADMIN_GROUP_NAME || 'administrator');
  const hasGroup = Array.isArray(groups) && groups.some(g => String(g).replace(/^\//, '') === ADMIN_NAME);
  // Policy: Only group membership confers admin rights
  const isAdministrator = hasGroup;

  // Backend probe fallback: if frontend cannot infer admin from token (e.g., PUBLIC_MODE or env mismatch),
  // attempt a lightweight call to an admin endpoint to decide whether to show admin links.
  useEffect(() => {
    let cancelled = false;
    const probe = async () => {
      if (isAdministrator) {
        if (!cancelled) setAdminOverride(true);
        return;
      }
      try {
        // Ensure API uses token when present
        if (keycloak?.token) {
          apiService.setToken(keycloak.token);
        }
        const resp = await apiService.getAdminMetrics();
        if (!cancelled && resp && resp.status === 200) {
          setAdminOverride(true);
        }
      } catch {
        if (!cancelled) setAdminOverride(false);
      }
    };
    probe();
    return () => { cancelled = true; };
  }, [isAdministrator, keycloak?.token]);

  // Fetch personal feed for notifications when authenticated
  useEffect(() => {
    let cancelled = false;
    if (!authenticated) {
      setFeedItems(null);
      setUnread(0);
      return;
    }
    (async () => {
      try {
        const resp = await apiService.getMyFeed(10, 0);
        if (cancelled) return;
        const items = Array.isArray(resp.data?.items) ? resp.data.items : [];
        setFeedItems(items);
        setUnread(items.length);
      } catch (e) {
        // Hide notifications if endpoint not available (feature flag off)
        setFeedItems([]);
        setUnread(0);
      }
    })();
    return () => { cancelled = true; };
  }, [authenticated]);

  const isAdminUI = isAdministrator || adminOverride;

  // Global health probe: display a maintenance banner when API or DB is unavailable
  useEffect(() => {
    let cancelled = false;
    let interval: any;
    const probe = async () => {
      try {
        const resp = await apiService.getHealthStatus();
        const ok = (resp?.status === 200) && (resp?.data?.status === 'healthy');
        if (!cancelled) setMaintenance({ unhealthy: !ok, detail: ok ? null : (resp?.data?.detail || 'unavailable') });
      } catch (e: any) {
        if (!cancelled) setMaintenance({ unhealthy: true, detail: 'network_or_server' });
      }
    };
    probe();
    interval = setInterval(probe, 20000);
    return () => { cancelled = true; if (interval) clearInterval(interval); };
  }, []);

  // Best-effort fetch of my profile handle to show a drawer link when available
  const [myProfileHandle, setMyProfileHandle] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!authenticated) { setMyProfileHandle(null); return; }
      try {
        const resp = await apiService.getMyProfile();
        const h = resp?.data?.profile?.handle || null;
        if (!cancelled) setMyProfileHandle(h);
      } catch {
        if (!cancelled) setMyProfileHandle(null);
      }
    })();
    return () => { cancelled = true; };
  }, [authenticated]);

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
    { text: 'Bills', icon: <BillIcon />, path: '/bills' },
    { text: 'Congresses', icon: <BillIcon />, path: '/congress' },
    { text: 'Civic Response Studio', icon: <ChatIcon />, path: '/civic-response-studio' },
    ...(authenticated ? [{ text: 'Bookmarks', icon: <BillIcon />, path: '/bookmarks' }] : [] as any),
    ...(authenticated ? [{ text: 'Saved Searches', icon: <SavedSearchIcon />, path: '/me/saved-searches' }] : [] as any),
    ...(authenticated ? [{ text: 'Collections', icon: <CollectionsIcon />, path: '/me/collections' }] : [] as any),
    ...(authenticated ? [{ text: 'Alerts', icon: <AlertsIcon />, path: '/me/alerts' }] : [] as any),
    ...(authenticated ? [{ text: 'Notes', icon: <NotesIcon />, path: '/me/notes' }] : [] as any),
    ...(authenticated ? [{ text: 'My Profile', icon: <PeopleIcon />, path: `/me/profile` }] : [] as any),
    { text: 'Committees', icon: <CommitteesIcon />, path: '/committees' },
    { text: 'Members', icon: <PeopleIcon />, path: '/members' },
    { text: 'Nominations', icon: <NominationsIcon />, path: '/nominations' },
    { text: 'AI Chat', icon: <ChatIcon />, path: '/chat' },
    { text: 'Embed Widgets', icon: <ChatIcon />, path: '/widgets' },
    ...(isAdminUI ? [
      { text: 'Admin', icon: <AdminIcon />, path: '/admin' },
      { text: 'AdSense', icon: <AdminIcon />, path: '/admin/adsense' },
      { text: 'Sync Inspector', icon: <AdminIcon />, path: '/admin/sync-inspector' },
    ] : [] as any),
  ];

  const drawer = (
    <div>
      <Toolbar>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <img src="/ask_the_bill.png" alt="AskTheBill.ai" style={{ height: 150, width: 200, objectFit: 'contain' }} />
        </Box>
      </Toolbar>
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => navigate(item.path)}
            >
              <ListItemIcon>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
            <Typography variant="h6" noWrap component="div">
              AskTheBill.ai
            </Typography>
          </Box>
          {authenticated && (
            <Typography variant="body2" sx={{ mr: 2, display: { xs: 'none', sm: 'block' } }}>
              {keycloak?.tokenParsed?.preferred_username}
            </Typography>
          )}
          {/* Notifications bell (authenticated only) */}
          {authenticated && Array.isArray(feedItems) && (
            <>
              <Tooltip title="Notifications">
                <IconButton color="inherit" onClick={(e) => { setNotifAnchor(e.currentTarget); setUnread(0); }} sx={{ mr: 1 }}>
                  <Badge badgeContent={unread} color="error">
                    <NotificationsIcon />
                  </Badge>
                </IconButton>
              </Tooltip>
              <Menu anchorEl={notifAnchor} open={Boolean(notifAnchor)} onClose={() => setNotifAnchor(null)}>
                <Typography variant="subtitle2" sx={{ px: 2, py: 1 }}>Your Feed</Typography>
                <Divider />
                {feedItems.length === 0 ? (
                  <MenuItem disabled>No new items</MenuItem>
                ) : (
                  feedItems.slice(0, 8).map((it: any, idx: number) => {
                    const b = it.bill || {};
                    const to = (b.congress && b.bill_type && b.bill_number) ? `/bills/${b.congress}/${b.bill_type}/${b.bill_number}` : null;
                    const label = b.title || `${(b.bill_type || '').toUpperCase()} ${b.bill_number}`;
                    return (
                      <MenuItem key={idx} onClick={() => { setNotifAnchor(null); if (to) navigate(to); }}>
                        <ListItemText primary={label} secondary={b.latest_action_text || b.latest_action_date} />
                      </MenuItem>
                    );
                  })
                )}
                <Divider />
                <MenuItem onClick={() => { setNotifAnchor(null); navigate('/notifications'); }}>View all notifications</MenuItem>
              </Menu>
            </>
          )}
          <Tooltip title="Toggle dark mode">
            <IconButton
              color="inherit"
              aria-label="toggle dark mode"
              onClick={() => window.dispatchEvent(new Event('toggle-dark-mode'))}
              sx={{ mr: 1 }}
            >
              {document.body?.dataset?.theme === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>
          </Tooltip>
          {!authenticated && (
            <Button
              color="inherit"
              onClick={() => {
                const PUBLIC_MODE = (process.env.REACT_APP_PUBLIC_MODE || 'false') === 'true';
                if (PUBLIC_MODE) {
                  navigate('/login');
                } else {
                  login();
                }
              }}
              startIcon={<LoginIcon />}
              sx={{ mr: 1 }}
            >
              Login
            </Button>
          )}
          {authenticated && (
            <Button
              color="inherit"
              onClick={logout}
              startIcon={<LogoutIcon />}
            >
              Logout
            </Button>
          )}
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, overflowX: 'hidden' },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, overflowX: 'hidden' },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          overflowX: 'clip',
          p: { xs: 1, sm: 3 },
          width: { sm: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        <Toolbar />
        {maintenance.unhealthy && (
          <Box sx={{ position: 'sticky', top: 0, zIndex: (t)=> t.zIndex.appBar - 1, mb: 2 }}>
            <Alert severity="warning">
              Site maintenance: Our servers are temporarily unavailable or the database cannot be reached. Some features may not work. We’ll restore service automatically.
            </Alert>
          </Box>
        )}
        {children}
        {/* Footer (public policy links for AdSense approval) */}
        <Divider sx={{ my: 4 }} />
        <Box component="footer" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', color: 'text.secondary', fontSize: 14 }}>
          <Button size="small" onClick={() => navigate('/about')}>About</Button>
          <Button size="small" onClick={() => navigate('/privacy')}>Privacy Policy</Button>
          <Button size="small" onClick={() => navigate('/terms')}>Terms & Conditions</Button>
          <Button size="small" onClick={() => navigate('/contact')}>Contact Us</Button>
          <Button size="small" onClick={() => navigate('/sitemap')}>Sitemap</Button>
          <Typography variant="caption" sx={{ ml: 'auto' }}>
            © {new Date().getFullYear()} AskTheBill.ai
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;
